import { VEV_EXCLUDED_STATIC_SELECTORS } from '@/constants/vevExcludedStaticSelectors';
import { Content } from '@/types/content';
import { getUrl } from '@/utils/getUrl';
import { http } from '@/utils/http';
import { removeBySelectors } from 'lib/labrador/content-operations/removeBy';
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppState } from './useAppState';

const removeStaticSelectorsFromVevArticle = (article: Content) => {
  if (article.meta.isVevArticle) article.children = removeBySelectors(VEV_EXCLUDED_STATIC_SELECTORS, article.children);
  return article;
};

export const useInfinityScrollArticle = ({
  intersectionTargetRef,
}: {
  intersectionTargetRef: RefObject<HTMLDivElement>;
}) => {
  const [{ device, scrollArticles }, appStateDispatcher] = useAppState();
  const [loadedArticles, setLoadedArticles] = useState<Content[]>([]);
  const [isLoading, setLoading] = useState(false);

  const loadNextArticle = useCallback(async () => {
    const url = getUrl('/api/page');
    const scrollArticle = scrollArticles[loadedArticles.length];

    if (!url || !scrollArticle || isLoading) return null;

    setLoading(true);

    const params = new URLSearchParams({ route: `article/${scrollArticle.article_id}`, device });
    url.search = params.toString();
    const response = await http.get<Content>(url.href);
    const data = response?.data;

    if (data) {
      data.meta.scrollArticleNumber = scrollArticles.findIndex((a) => a.article_id === scrollArticle.article_id) + 1;
      data.data.analyticsData.ctsData.scrollArticleModel = scrollArticle.model;
      data.data.analyticsData.ctsData.scrollArticleType = scrollArticle.type;
      setLoadedArticles((prevLoadedArticles) => [...prevLoadedArticles, removeStaticSelectorsFromVevArticle(data)]);
    }

    if (!data) {
      appStateDispatcher({ scrollArticles: scrollArticles.slice(1) });
    }

    setLoading(false);

    return data;
  }, [scrollArticles, loadedArticles, device, isLoading]);

  useEffect(() => {
    if (!scrollArticles.length || scrollArticles.length === loadedArticles.length || !intersectionTargetRef.current) {
      appStateDispatcher({ activeInfinityScrollArticle: false });
      return;
    }
    appStateDispatcher({ activeInfinityScrollArticle: true });

    const observer = new IntersectionObserver(([entry]) => {
      if ((entry.isIntersecting || entry.boundingClientRect.y < 0) && !isLoading) {
        loadNextArticle().then((data) => {
          if (!data) {
            observer.disconnect();
            appStateDispatcher({ activeInfinityScrollArticle: false });
          }
        });
      }
    });

    observer.observe(intersectionTargetRef.current as Element);

    return () => observer.disconnect();
  }, [scrollArticles, isLoading, loadNextArticle, loadedArticles]);

  return useMemo(() => ({ articles: loadedArticles, isLoading }), [loadedArticles, isLoading]);
};
